'use client';

import { c as _c } from "react/compiler-runtime";
import type { CSSProperties } from 'react';
import { useTranslations } from 'next-intl';
import LogoOverlayRecommendationCarousel from '@/app/[locale]/(worlds)/[roomId]/components/LogoOverlayRecommendationCarousel';
import LogoOverlayViewAllCard from '@/app/[locale]/(worlds)/[roomId]/components/LogoOverlayViewAllCard';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { LOGO_OVERLAY_RECOMMENDATIONS_LIMIT } from '@/constants/recommend';
import { useWorldDetailsContext } from '@/context/WorldDetailsContext';
import { useIsEmbedded } from '@/hooks/useIsEmbedded';
import { LogoOverlayContentKind, type LogoOverlayContentModel } from '@/lib/logo-overlay';
import { withFull3d } from '@/lib/url';
import type { RecommendRoom } from '@/services/cms';
const overlayCardWidth = 'var(--overlay-card-w, 152px)';
type LogoOverlayRecommendationsProps = {
  overlayModel: LogoOverlayContentModel;
  rooms: RecommendRoom[];
  /** Whether to show room title below thumbnail. Computed by parent: experience kind + isFullLayout. */
  cardOnly: boolean;
  showArrows: boolean;
  /** Horizontal alignment derived from logo position. */
  alignment: 'left' | 'center' | 'right';
};
export default function LogoOverlayRecommendations(t0) {
  const $ = _c(28);
  const {
    overlayModel,
    rooms,
    cardOnly,
    showArrows,
    alignment
  } = t0;
  const t = useTranslations();
  const {
    isEmbeddedRequest
  } = useWorldDetailsContext();
  const isEmbedded = useIsEmbedded(isEmbeddedRequest);
  const {
    contentKind,
    viewAllHref,
    viewAllLabelKey: viewAllLabel
  } = overlayModel;
  if (rooms.length === 0) {
    if (!viewAllHref || !viewAllLabel) {
      return null;
    }
    let t1;
    if ($[0] !== t || $[1] !== viewAllLabel) {
      t1 = t(viewAllLabel);
      $[0] = t;
      $[1] = viewAllLabel;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== t1 || $[4] !== viewAllHref) {
      t2 = <LogoOverlayViewAllCard label={t1} href={viewAllHref} />;
      $[3] = t1;
      $[4] = viewAllHref;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== alignment || $[7] !== showArrows || $[8] !== t2) {
      t3 = <LogoOverlayRecommendationCarousel showArrows={showArrows} alignment={alignment}>{t2}</LogoOverlayRecommendationCarousel>;
      $[6] = alignment;
      $[7] = showArrows;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  const isExperience = contentKind === LogoOverlayContentKind.EXPERIENCE;
  let t1;
  if ($[10] !== cardOnly || $[11] !== isEmbedded || $[12] !== isExperience || $[13] !== rooms) {
    let t2;
    if ($[15] !== cardOnly || $[16] !== isEmbedded || $[17] !== isExperience) {
      t2 = (room, index) => {
        const href = room.hubs_url || `/${room.hub_sid}`;
        return <ViverseWorldCard key={room.id} info={room} href={isEmbedded ? withFull3d(href) : href} cardOnly={cardOnly} cardType={isExperience ? "experiences" : "games"} hideLiked={true} hidePremiumBadge={true} disableAutoPreview={true} showMoreAction={false} imageSizes="clamp(96px, 10.56vw, 152px)" className="animate-logo-overlay-card-enter shrink-0" style={{
          width: !cardOnly ? `calc(${overlayCardWidth} + 16px)` : overlayCardWidth,
          "--card-delay": `${50 + index * 20}ms`
        } as CSSProperties} />;
      };
      $[15] = cardOnly;
      $[16] = isEmbedded;
      $[17] = isExperience;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    t1 = rooms.slice(0, LOGO_OVERLAY_RECOMMENDATIONS_LIMIT).map(t2);
    $[10] = cardOnly;
    $[11] = isEmbedded;
    $[12] = isExperience;
    $[13] = rooms;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[19] !== t || $[20] !== viewAllHref || $[21] !== viewAllLabel) {
    t2 = viewAllHref && viewAllLabel && <LogoOverlayViewAllCard label={t(viewAllLabel)} href={viewAllHref} />;
    $[19] = t;
    $[20] = viewAllHref;
    $[21] = viewAllLabel;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== alignment || $[24] !== showArrows || $[25] !== t1 || $[26] !== t2) {
    t3 = <LogoOverlayRecommendationCarousel showArrows={showArrows} alignment={alignment}>{t1}{t2}</LogoOverlayRecommendationCarousel>;
    $[23] = alignment;
    $[24] = showArrows;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
  } else {
    t3 = $[27];
  }
  return t3;
}